import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Award, Loader2, Check, X, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

/**
 * Public landing reached from the email-invite link.
 *
 * Anonymous flow:
 *   1. Show "who invited you" preview (no auth needed)
 *   2. If user is not logged in → CTA "Sign in / Sign up to accept"
 *      We stash the token in localStorage so after auth they can land back here.
 *   3. Once authed → POST /trainer-invite/{token}/claim, then navigate to /trainer
 */
export default function TrainerInviteLanding() {
  const { token } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [claiming, setClaiming] = useState(false);
  const [claimed, setClaimed] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/trainer-invite/${token}/preview`);
        setPreview(data);
      } catch (e) {
        setError(e.response?.data?.detail || "Invite link is invalid or already used.");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const claim = async () => {
    setClaiming(true);
    try {
      await api.post(`/trainer-invite/${token}/claim`);
      setClaimed(true);
      toast.success("You're connected — opening Trainer Hub");
      setTimeout(() => navigate("/trainer"), 1000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't accept invite");
    } finally {
      setClaiming(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-md mx-auto p-8 text-center text-sm text-neutral-400 flex items-center justify-center gap-2 mt-20">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading invite…
      </div>
    );
  }
  if (error) {
    return (
      <div data-testid="invite-error" className="max-w-md mx-auto p-8 text-center mt-20">
        <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
        <h1 className="font-display text-2xl uppercase mb-2">Invite Unavailable</h1>
        <p className="text-sm text-neutral-400 mb-4">{error}</p>
        <Link to="/"><Button variant="outline" className="border-neutral-700 hover:border-blue-500 rounded-none uppercase tracking-wider font-display">Back home</Button></Link>
      </div>
    );
  }

  return (
    <div data-testid="invite-landing" className="max-w-md mx-auto p-6 pt-16">
      <div className="flex items-center gap-2 mb-1 text-xs uppercase tracking-[0.3em] text-blue-500 font-bold">
        <Award className="w-3 h-3" /> Trainer Invitation
      </div>
      <h1 className="font-display text-3xl uppercase mb-3">You've been invited</h1>
      <div className="border border-blue-500/40 bg-blue-500/5 p-5 mb-4">
        <div className="text-sm text-neutral-300">
          <strong data-testid="invite-from-name" className="text-blue-500">{preview?.from_client_name}</strong> wants you to be their trainer on <strong>GYM with GRIZZ</strong>.
        </div>
        {preview?.message && (
          <blockquote className="border-l-2 border-blue-500 pl-3 mt-3 text-sm italic text-neutral-200" data-testid="invite-message">
            "{preview.message}"
          </blockquote>
        )}
      </div>

      {!user ? (
        <>
          <p className="text-xs text-neutral-500 mb-3">Sign in or create an account to accept. You'll be auto-marked as a Trainer.</p>
          <Link to={`/signup?next=/trainer-invite/${token}`}>
            <Button data-testid="invite-signup-btn" className="w-full bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-11 mb-2">
              Create Trainer Account
            </Button>
          </Link>
          <Link to={`/login?next=/trainer-invite/${token}`}>
            <Button variant="outline" data-testid="invite-login-btn" className="w-full border-neutral-700 hover:border-blue-500 rounded-none uppercase tracking-wider font-display h-11">
              I have an account
            </Button>
          </Link>
        </>
      ) : claimed ? (
        <div className="flex items-center gap-2 text-sm text-green-500">
          <Check className="w-4 h-4" /> Connected. Redirecting to Trainer Hub…
        </div>
      ) : (
        <>
          <Button
            data-testid="invite-accept-btn"
            onClick={claim}
            disabled={claiming}
            className="w-full bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-11 mb-2"
          >
            {claiming ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
            Accept Invitation
          </Button>
          <Link to="/">
            <Button variant="ghost" data-testid="invite-skip-btn" className="w-full text-neutral-500 rounded-none uppercase tracking-wider font-display h-10">
              <X className="w-4 h-4 mr-2" /> Not now
            </Button>
          </Link>
        </>
      )}
    </div>
  );
}
